import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { AdjustInventoryModal, errText } from "./AdjustInventoryModal";

export const STATUS_STYLES = {
  AVAILABLE: "bg-emerald-100 text-emerald-700", LOW_STOCK: "bg-yellow-100 text-yellow-800", OUT_OF_STOCK: "bg-slate-200 text-slate-700",
  HELD: "bg-blue-100 text-blue-700", RESERVED: "bg-indigo-100 text-indigo-700", PARTIALLY_RESERVED: "bg-indigo-50 text-indigo-700",
  DAMAGED: "bg-red-100 text-brand-red", INACTIVE: "bg-slate-100 text-slate-500",
};
export const StatusPill = ({ status }) => (
  <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded whitespace-nowrap ${STATUS_STYLES[status] || "bg-slate-100"}`} data-testid="inventory-status-pill">{(status || "").replace(/_/g, " ")}</span>
);
export const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const InventoryHistoryModal = ({ row, onClose }) => {
  const [txs, setTxs] = useState(null);
  useEffect(() => {
    api.get("/inventory/transactions", { params: { inventory_id: row.inventory.inventory_id, per_page: 100 } }).then((r) => setTxs(r.data.items));
  }, [row]);
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="history-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 mt-10">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-brand-red font-black uppercase tracking-widest text-xs">Transaction history</div>
            <div className="font-heading text-xl text-brand-navy">{row.product.product_name}</div>
            <div className="text-xs text-slate-500">SKU {row.product.sku}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-brand-red font-bold" data-testid="history-close">Close</button>
        </div>
        <TxTable txs={txs} compact />
      </div>
    </div>
  );
};

export const TxTable = ({ txs, compact = false }) => (
  <div className="overflow-x-auto border border-slate-200 rounded-xl">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr>
          <th className="p-2">Date/time</th>{!compact && <th className="p-2">Product</th>}{!compact && <th className="p-2">SKU</th>}
          <th className="p-2">Action</th><th className="p-2">Change</th><th className="p-2">Before → After</th><th className="p-2">Reason / Notes</th><th className="p-2">User</th>
        </tr>
      </thead>
      <tbody>
        {!txs ? <tr><td colSpan={8} className="p-4 text-center text-slate-400">Loading...</td></tr> :
          txs.length === 0 ? <tr><td colSpan={8} className="p-4 text-center text-slate-400">No transactions yet</td></tr> :
            txs.map((t) => (
              <tr key={t.transaction_id} className="border-t border-slate-100" data-testid={`tx-row-${t.transaction_id}`}>
                <td className="p-2 whitespace-nowrap text-slate-500">{fmtDate(t.created_at)}</td>
                {!compact && <td className="p-2 font-bold">{t.product_name}</td>}
                {!compact && <td className="p-2 text-slate-500">{t.sku}</td>}
                <td className="p-2"><span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-slate-100">{t.transaction_type.replace(/_/g, " ")}</span></td>
                <td className={`p-2 font-bold ${t.quantity_change < 0 ? "text-brand-red" : "text-emerald-600"}`}>{t.quantity_change > 0 ? "+" : ""}{t.quantity_change}</td>
                <td className="p-2 text-slate-600">{t.quantity_before} → {t.quantity_after}</td>
                <td className="p-2 text-slate-600">{t.reason}{t.notes && <span className="text-slate-400"> · {t.notes}</span>}</td>
                <td className="p-2">{t.user_name}</td>
              </tr>
            ))}
      </tbody>
    </table>
  </div>
);

export const InventoryDetailModal = ({ row, onClose, onChanged, canSeeCost }) => {
  const inv = row.inventory;
  const [f, setF] = useState({ reorder_level: inv.reorder_level, barcode: inv.barcode || "", inventory_type: inv.inventory_type, ...inv.storage_location });
  const save = async () => {
    try {
      await api.put(`/inventory/${inv.inventory_id}`, {
        reorder_level: Number(f.reorder_level), barcode: f.barcode, inventory_type: f.inventory_type,
        storage_location: { aisle: f.aisle, rack: f.rack, shelf: f.shelf, bin: f.bin },
      });
      toast.success("Inventory details saved"); onChanged();
    } catch (e) { toast.error(errText(e)); }
  };
  const Q = ({ label, v }) => <div className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] uppercase font-black text-slate-500">{label}</div><div className="font-heading text-lg text-brand-navy">{v}</div></div>;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="inventory-detail-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 mt-10 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-brand-red font-black uppercase tracking-widest text-xs">Inventory record</div>
            <div className="font-heading text-xl text-brand-navy">{row.product.product_name}</div>
            <div className="text-xs text-slate-500">SKU {row.product.sku} · {row.location_name} · <StatusPill status={inv.inventory_status} /></div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-brand-red font-bold" data-testid="detail-close">Close</button>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Q label="On hand" v={inv.quantity_on_hand} /><Q label="Available" v={inv.quantity_available} /><Q label="Held" v={inv.quantity_held} /><Q label="Reserved" v={inv.quantity_reserved} />
          <Q label="Sold" v={inv.quantity_sold} /><Q label="Damaged" v={inv.quantity_damaged} /><Q label="Returned" v={inv.quantity_returned} />
          {canSeeCost && <Q label="Value" v={money((row.product.cost || 0) * inv.quantity_on_hand)} />}
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Reorder level" type="number" value={f.reorder_level} onChange={(v) => setF({ ...f, reorder_level: v })} testId="detail-reorder" />
          <Field label="Barcode (UPC/EAN/internal)" value={f.barcode} onChange={(v) => setF({ ...f, barcode: v })} testId="detail-barcode" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Inventory type</span>
            <select value={f.inventory_type} onChange={(e) => setF({ ...f, inventory_type: e.target.value })} data-testid="detail-type"
              className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-2 bg-white">
              <option>STANDARD</option><option>UNIQUE_ITEM</option>
            </select>
          </label>
        </div>
        <div>
          <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Storage location (internal)</div>
          <div className="grid grid-cols-4 gap-2">
            {["aisle", "rack", "shelf", "bin"].map((k) => (
              <input key={k} placeholder={k[0].toUpperCase() + k.slice(1)} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                data-testid={`detail-${k}`} className="h-10 rounded-lg border border-slate-200 px-3" />
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-400">Created {fmtDate(inv.created_at)} · Updated {fmtDate(inv.updated_at)} {inv.is_demo && <span className="ml-2 bg-brand-yellowLight text-brand-navy px-2 py-0.5 rounded font-black">DEMO DATA</span>}</div>
        <button onClick={save} className="bg-brand-navy text-white rounded-full px-6 h-11 font-bold" data-testid="detail-save">Save details</button>
      </div>
    </div>
  );
};

export const Field = ({ label, value, onChange, type = "text", testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3" />
  </label>
);

export { AdjustInventoryModal };
